import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import AuthLayout from '@/components/layout/AuthLayout'
import { adminAPI } from '@/lib/api/admin'
import { useAuthStore } from '@/hooks/useAuthStore'
import { decodeJwtPayload } from '@certi/shared/api/auth/jwt-utils'

interface LoginFormData {
  email: string
  password: string
}

export default function AdminLoginPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const data = await adminAPI.login(formData)
      console.log('Admin login response:', data) // Debug log
      
      // Validate JWT token and extract roles
      const payload = decodeJwtPayload(data.accessToken)
      console.log('Decoded JWT payload:', payload) // Debug log
      
      const roles = payload?.roles ?? []
      if (!roles.includes('ADMIN')) {
        throw new Error('This account does not have admin access')
      }
      
      // Create user object using real JWT data
      const adminUser = {
        id: payload?.sub ?? 'admin-user',
        email: formData.email,
        role: 'ADMIN'
      }
      console.log('Created admin user object:', adminUser) // Debug log
      
      // Store admin token and user data in localStorage (for admin API calls)
      localStorage.setItem('authToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)
      localStorage.setItem('user', JSON.stringify(adminUser))
      console.log('Stored in localStorage') // Debug log
      
      // Also update the auth store so ProtectedRoute recognizes the user
      const authStoreUpdate = {
        user: {
          id: adminUser.id,
          email: adminUser.email,
          firstName: adminUser.email.split('@')[0], // Extract name from email
          lastName: '',
          phone: '',
          status: 'ACTIVE',
          roles: roles, // Use actual roles from JWT
          createdAt: new Date().toISOString()
        },
        session: {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiresIn: data.expiresIn || 3600
        },
        accessToken: data.accessToken,
        loading: false,
        error: undefined
      }
      console.log('About to update auth store with:', authStoreUpdate) // Debug log
      useAuthStore.setState(authStoreUpdate)
      console.log('Auth store updated, navigating...') // Debug log
      
      // Navigate to admin dashboard
      navigate('/admin/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Admin login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout title="Admin Login" subtitle="Sign in to admin dashboard">
      {/* Admin Badge */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 mb-2">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
          </div>
          <span className="px-2 py-1 bg-purple-600 text-white text-xs font-medium rounded">
            Admin
          </span>
        </div>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div>
          <Input
            type="email"
            name="email"
            placeholder="Admin Email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div>
          <Input
            type="password"
            name="password"
            placeholder="Admin Password"
            value={formData.password}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign in to Admin'}
        </Button>
      </form>

      {/* Footer */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          Need regular access?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            User Login
          </button>
        </p>
      </div>
    </AuthLayout>
  )
}